/** @prettier */

import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { CamPlayer, useDeviceSize } from '@surfline/quiver-react';
import classNames from 'classnames';
import { motion, useAnimation } from 'framer-motion';
import SpotThumbnailBackground from '@surfline/quiver-react/dist/SpotThumbnailBackground';

import createAccessibleOnClick, { BTN } from '../../utils/createAccessibleOnClick';
import { camLoadingVariants, camVariants } from './variants';
import { useFramerAnimation } from '../../hooks/useFramerAnimation';
import { useToggle } from '../../hooks/useToggle';
import spotPropTypes, { spotDefaultProps } from '../../propTypes/spot';
import { getNumCams } from '../../selectors/multiCam';
import { trackEvent } from '../../actions/segmentEvents';
import { getUserType, IS_BASIC } from '../../selectors/user';
import CamOverlay from './CamOverlay';
import { useSetCamAngleIndex } from './hooks/useSaveMultiCamState';

const camClassName = isPrimary =>
  classNames({
    'sl-multi-cam__cam': true,
    'sl-multi-cam__cam--primary': isPrimary,
  });

const getCamContainerClass = (camIndex, isDragging, draggable) =>
  classNames({
    'sl-multi-cam__cam-container': true,
    [`sl-multi-cam__cam-container--${camIndex + 1}`]: true,
    'sl-multi-cam__cam-container--dragging': isDragging,
    'sl-multi-cam__cam-container--no-drag': !draggable,
    'sl-multi-cam__cam-container--draggable': draggable,
  });

const Cam = React.memo(
  ({
    spot,
    camIndex,
    primarySpotIndex,
    canMountCams,
    isPlaying,
    onClick,
    camsRef,
    showingAdPlayer,
    showingPaywall,
    showingRegwall,
    showingUpSell,
  }) => {
    const defaultCamAngleIndex = spot?.selectedCamAngleIndex || 0;
    // State
    const [camAngle, setCamAngle] = useState(spot?.cameras?.[defaultCamAngleIndex]);
    const [camReady, setCamReady] = useToggle();
    const [isDragging, setDragging] = useState(false);
    const device = useDeviceSize();

    // Redux
    const dispatch = useDispatch();
    const numCams = useSelector(getNumCams);
    const userType = useSelector(getUserType);
    const setCamAngleIndex = useSetCamAngleIndex();

    // Constants
    const hasCam = !!spot?.cameras?.length;
    const isPayWalled = showingPaywall || showingRegwall;
    const showPremiumCamCTA = !isPayWalled && userType === IS_BASIC && camAngle?.isPremium;
    const showingCam = !isPayWalled && !showPremiumCamCTA && !showingAdPlayer;
    const { isFiller } = spot;
    const draggable = camIndex === 1 && numCams === 2;
    const isPrimary = camIndex === primarySpotIndex;
    const showCamStill = hasCam && (isPayWalled || showingUpSell) && !showPremiumCamCTA;

    // Cam Animations
    const camControls = useAnimation();
    const [camAnimationDone, setCamAnimationDone] = useFramerAnimation({
      controls: camControls,
      variant: 'visible',
      start: canMountCams && hasCam && camReady && showingCam,
      log: true,
    });
    const showCamLoadingGradient = hasCam && !camAnimationDone && !isFiller && showingCam;

    /**
     * @description resets the cam animation state and sets the new cam angle.
     * @param {object} cam Cam object
     * @param {string} cam._id
     */
    const changeCamAngle = cam => {
      camControls.set('hidden');
      setCamAnimationDone(false);
      setCamReady(false);
      setCamAngle(cam);
      const camAngleIndex = spot?.cameras.findIndex(camera => camera._id === cam._id);
      setCamAngleIndex(camIndex, camAngleIndex);
      dispatch(
        trackEvent('Toggled Cam View', {
          category: 'cams & reports',
          name: 'multi-cam - angle',
          subregionId: spot.subregionId,
          spotId: spot._id,
          camId: cam._id,
          camName: cam.title,
          isMobileView: device.width < 976,
          view: numCams > 1 ? 'MULTI' : 'SINGLE',
          camNumber: numCams,
          subscriptionEntitlement: userType,
        })
      );
    };

    return (
      <motion.div
        drag={draggable}
        dragConstraints={camsRef}
        dragElastic={0.3}
        dragMomentum={false}
        onDragStart={() => setDragging(true)}
        onDragEnd={() => setDragging(false)}
        className={getCamContainerClass(camIndex, isDragging, draggable)}
      >
        <div
          type="button"
          className={camClassName(isPrimary)}
          {...createAccessibleOnClick(() => !isPrimary && onClick(), BTN)}
        >
          {showCamLoadingGradient && (
            <motion.div
              className="sl-multi-cam__loading-gradient"
              animate="animate"
              initial="initial"
              variants={camLoadingVariants}
            />
          )}
          {!hasCam && <SpotThumbnailBackground spot={spot} />}
          {hasCam && showingCam && (
            <motion.div
              className="sl-multi-cam__cams_cam"
              variants={camVariants}
              initial="hidden"
              animate={camControls}
            >
              <CamPlayer
                controlledAds
                controlledPlayback
                handleCamReady
                camReady={camReady}
                setCamReady={setCamReady}
                playerId={`${camAngle?._id}${camIndex}`}
                camera={camAngle}
                spotId={spot?._id}
                spotName={spot?.name}
                isPlaying={isPlaying}
                isMultiCam
              />
            </motion.div>
          )}
          {showCamStill && (
            <div
              className="sl-cam-still"
              style={{ backgroundImage: `url(${camAngle?.stillUrl})` }}
            />
          )}
          {(!showingAdPlayer || (showingAdPlayer && showingUpSell)) && (
            <CamOverlay
              changeCamAngle={changeCamAngle}
              camAngle={camAngle}
              camIndex={camIndex}
              spot={spot}
              showingPremiumCamCTA={showPremiumCamCTA}
            />
          )}
        </div>
      </motion.div>
    );
  }
);

Cam.propTypes = {
  spot: spotPropTypes,
  camIndex: PropTypes.number.isRequired,
  primarySpotIndex: PropTypes.number.isRequired,
  canMountCams: PropTypes.bool.isRequired,
  isPlaying: PropTypes.bool,
  onClick: PropTypes.func,
  isPremium: PropTypes.bool,
  camsRef: PropTypes.shape({ current: PropTypes.any }),
  isPlayingAd: PropTypes.bool,
  showingAdPlayer: PropTypes.bool,
  showingPaywall: PropTypes.bool,
  showingRegwall: PropTypes.bool,
  showingUpSell: PropTypes.bool,
};

Cam.defaultProps = {
  spot: {
    ...spotDefaultProps,
    isFiller: true,
  },
  isPlaying: false,
  onClick: () => null,
  isPremium: true,
  isPlayingAd: false,
  camsRef: null,
  showingAdPlayer: false,
  showingPaywall: false,
  showingRegwall: false,
  showingUpSell: false,
};

export default Cam;
